<template>
    <div class="fill-height d-flex flex-column">
        <v-data-table
            :headers="headers"
            :items="upcomingEventsFiltered"
            class="elevation-0 flex-grow-1"
            :mobile-breakpoint="0"
            :loading="loading"
            :height="tableHeight"
            fixed-header
            :item-class="getRowClass"
            :footer-props="{
                itemsPerPageOptions: [15, 30, 100, -1],
                itemsPerPageText: 'Work Orders Per Page',
            }"
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 py-0"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" class="d-flex mb-n3">
                        <h1 class="mr-4">UPCOMING EVENTS</h1>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.packingListCode`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="searchPackingListInput"
                />
            </template>
            <template v-slot:[`header.project`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="searchProjectInput"
                />
            </template>
            <template v-slot:[`header.projectManager`]="{ header }">
                <v-text-field
                    class="py-2 mt-2"
                    :label="header.text"
                    v-model="searchProjectManagerInput"
                />
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.packingListCode`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{ item.packingList.code }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.project`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{
                            item.project
                                ? `${item.project?.reference} - ${item.project?.name}`
                                : ''
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.projectManager`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{
                            item.projectManager ? item.projectManager.name : ''
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.status`]="{ item }">
                <div class="d-flex justify-center">
                    <v-chip
                        :color="statusColor[item.packingList.status]"
                        text-color="white"
                    >
                        {{ item.packingList.status }}
                    </v-chip>
                </div>
            </template>
            <template v-slot:[`item.requestedShipmentDate`]="{ item }">
                <div class="d-flex justify-center">
                    <p class="my-0">
                        {{
                            item.packingList.requestedShipmentDate
                                ? formatDate(
                                      item.packingList.requestedShipmentDate,
                                      'L'
                                  )
                                : 'Not specified'
                        }}
                    </p>
                </div>
            </template>
            <template v-slot:[`item.important`]="{ item }">
                <div class="d-flex justify-center">
                    <v-icon v-if="item.packingList.important" color="red" small>
                        mdi-flag
                    </v-icon>
                </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn small icon @click="openProcessPackingList(item)">
                        <v-icon>{{
                            item.packingList.status !== 'completed'
                                ? 'mdi-pencil'
                                : 'mdi-eye'
                        }}</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>
        <!--PROCESS PACKING ORDER-->
        <v-dialog
            v-model="processPackingList"
            :retain-focus="false"
            persistent
            fullscreen
        >
            <ProcessPackingPending
                v-if="processPackingList && selectedOrder.status == 'pending'"
                :originalOrder="selectedOrder"
                :project="selectedProject"
                :onlySuggested="!createPackingListPermission"
                @replacePackingList="replacePackingList"
                @close="closeProcessPackingList"
                @addPackingList="addPackingList"
            />
            <ProcessPackingScheduled
                v-if="processPackingList && selectedOrder.status == 'scheduled'"
                :originalOrder="selectedOrder"
                :project="selectedProject"
                :onlySuggested="!createPackingListPermission"
                @replacePackingList="replacePackingList"
                @close="closeProcessPackingList"
            />
            <ProcessPackingReferred
                v-if="processPackingList && selectedOrder.status == 'referred'"
                :originalOrder="selectedOrder"
                :project="selectedProject"
                :onlySuggested="!createPackingListPermission"
                @replacePackingList="replacePackingList"
                @close="closeProcessPackingList"
            />
            <ProcessPackingShipped
                v-if="processPackingList && selectedOrder.status == 'shipped'"
                :originalOrder="selectedOrder"
                :project="selectedProject"
                :onlySuggested="!createPackingListPermission"
                @replacePackingList="replacePackingList"
                @close="closeProcessPackingList"
            />
            <ProcessPackingCompleted
                v-if="processPackingList && selectedOrder.status == 'completed'"
                :originalOrder="selectedOrder"
                :project="selectedProject"
                :onlySuggested="!createPackingListPermission"
                @replacePackingList="replacePackingList"
                @close="closeProcessPackingList"
            />
        </v-dialog>
        <!--PACKING ORDER FORM-->
        <v-dialog
            v-model="packingListForm"
            :retain-focus="false"
            persistent
            fullscreen
        >
            <PackingForm
                v-if="packingListForm"
                :project="selectedProject"
                :originalOrder="selectedOrder"
                :onlySuggested="!createPackingListPermission"
                @close="closePackingListForm"
                @addPackingList="addPackingList"
                @replacePackingList="replacePackingList"
            />
        </v-dialog>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapMutations } from 'vuex'
import moment from 'moment'
import API from '@/services/api'
import { firestore } from '@/services/firebase'

export default {
    name: 'UpcomingEvents',
    components: {
        PackingForm: () => import('@/components/Packing/PackingForm.vue'),
        ProcessPackingPending: () =>
            import('@/components/Packing/ProcessPackingPending.vue'),
        ProcessPackingScheduled: () =>
            import('@/components/Packing/ProcessPackingScheduled.vue'),
        ProcessPackingReferred: () =>
            import('@/components/Packing/ProcessPackingReferred.vue'),
        ProcessPackingShipped: () =>
            import('@/components/Packing/ProcessPackingShipped.vue'),
        ProcessPackingCompleted: () =>
            import('@/components/Packing/ProcessPackingCompleted.vue'),
    },
    data: () => ({
        loading: true,
        packingLists: [],
        packingListsListener: undefined,
        companyId: JSON.parse(localStorage.getItem('company')),
        projects: [],
        users: [],
        searchPackingList: '',
        searchProject: '',
        searchProjectManager: '',
        searchPackingListInput: '',
        searchProjectInput: '',
        searchProjectManagerInput: '',
        selectedOrder: undefined,
        selectedProject: {},
        processPackingList: false,
        packingListForm: false,
        createPackingListPermission: false,
        user: {},
        headers: [
            {
                text: 'PACKING LIST',
                align: 'center',
                sortable: false,
                value: 'packingListCode',
            },
            {
                text: 'PROJECT',
                align: 'center',
                sortable: false,
                value: 'project',
            },
            {
                text: 'PROJECT MANAGER',
                align: 'center',
                sortable: false,
                value: 'projectManager',
            },
            {
                text: 'STATUS',
                align: 'center',
                sortable: false,
                value: 'status',
            },
            {
                text: 'REQUESTED SHIPMENT DATE',
                align: 'center',
                sortable: false,
                value: 'requestedShipmentDate',
            },
            {
                text: '',
                align: 'center',
                sortable: false,
                value: 'important',
                width: 60,
            },
            {
                text: 'ACTIONS',
                align: 'center',
                sortable: false,
                value: 'actions',
            },
        ],
        statusColor: {
            inProgress: '#9E9FA0',
            pending: '#FF9800',
            referred: 'primary',
            scheduled: '#25539d',
            rejected: 'red',
            completed: 'black',
            shipped: '#27BC4F',
            reserved: '#92cbee',
            reservedForProject: '#184b81',
            domesticShipping: '#1d283a',
        },
        windowHeight: typeof window !== 'undefined' ? window.innerHeight : 800,
    }),
    computed: {
        upcomingEventsFiltered() {
            return this.packingLists
                .filter(packingList => {
                    // Filter out packing lists that have scheduledFor or shippedOn
                    if (
                        packingList.scheduledFor ||
                        packingList.shippedOn ||
                        packingList.completedOn
                    ) {
                        return false
                    }

                    // Apply search filters
                    const packingListMatch =
                        !this.searchPackingList ||
                        packingList.code
                            .toLowerCase()
                            .includes(this.searchPackingList.toLowerCase())

                    const project = this.projects.find(
                        p => p.id === packingList.projectId
                    )
                    const projectMatch =
                        !this.searchProject ||
                        (project &&
                            ((project.reference &&
                                String(project.reference)
                                    .toLowerCase()
                                    .includes(
                                        this.searchProject.toLowerCase()
                                    )) ||
                                (project.name &&
                                    String(project.name)
                                        .toLowerCase()
                                        .includes(
                                            this.searchProject.toLowerCase()
                                        ))))

                    const projectManager = this.users.find(
                        u => u.id === project?.projectManager
                    )
                    const projectManagerMatch =
                        !this.searchProjectManager ||
                        (projectManager &&
                            projectManager.name &&
                            String(projectManager.name)
                                .toLowerCase()
                                .includes(
                                    this.searchProjectManager.toLowerCase()
                                ))

                    return (
                        packingListMatch && projectMatch && projectManagerMatch
                    )
                })
                .map(packingList => {
                    const project = this.projects.find(
                        p => p.id === packingList.projectId
                    )
                    const projectManager = this.users.find(
                        u => u.id === project?.projectManager
                    )

                    return {
                        packingList,
                        project,
                        projectManager,
                    }
                })
                .sort((a, b) => {
                    // First, prioritize important packing lists
                    const aImportant = a.packingList.important || false
                    const bImportant = b.packingList.important || false

                    if (aImportant && !bImportant) {
                        return -1 // a comes first
                    }
                    if (!aImportant && bImportant) {
                        return 1 // b comes first
                    }

                    // If both are important or both are not important,
                    // sort by requested shipment date if available, otherwise by packing list code
                    if (
                        a.packingList.requestedShipmentDate &&
                        b.packingList.requestedShipmentDate
                    ) {
                        const dateA = new Date(
                            a.packingList.requestedShipmentDate
                        )
                        const dateB = new Date(
                            b.packingList.requestedShipmentDate
                        )
                        return dateA - dateB
                    } else if (a.packingList.requestedShipmentDate) {
                        return -1
                    } else if (b.packingList.requestedShipmentDate) {
                        return 1
                    } else {
                        return a.packingList.code.localeCompare(
                            b.packingList.code
                        )
                    }
                })
        },
        tableHeight() {
            const windowHeight = this.windowHeight || window.innerHeight
            const headerHeight = this.$vuetify.breakpoint.mdAndDown ? 80 : 70
            const padding = 40
            const calculatedHeight = windowHeight - headerHeight - padding
            const minHeight = 400
            const maxHeight = windowHeight * 0.9
            return (
                Math.max(minHeight, Math.min(calculatedHeight, maxHeight)) +
                'px'
            )
        },
    },

    created() {
        // Create debounced functions for search filters
        this.debouncedUpdatePackingList = _.debounce(value => {
            this.searchPackingList = value
        }, 400)

        this.debouncedUpdateProject = _.debounce(value => {
            this.searchProject = value
        }, 400)

        this.debouncedUpdateProjectManager = _.debounce(value => {
            this.searchProjectManager = value
        }, 400)
    },

    async mounted() {
        try {
            this.loading = true
            const {
                data: { user },
            } = await API.getMyInfo()
            this.user = user
            this.createPackingListPermission = this.user.permissions.includes(
                'createPackingList'
            )
            await this.loadData()
            this.handleResize = () => {
                this.windowHeight = window.innerHeight
            }
            window.addEventListener('resize', this.handleResize)
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    beforeDestroy() {
        if (this.packingListsListener) {
            this.packingListsListener()
            this.packingListsListener = null
        }
        if (this.handleResize) {
            window.removeEventListener('resize', this.handleResize)
            this.handleResize = null
        }
    },

    watch: {
        searchPackingListInput(value) {
            this.debouncedUpdatePackingList(value)
        },
        searchProjectInput(value) {
            this.debouncedUpdateProject(value)
        },
        searchProjectManagerInput(value) {
            this.debouncedUpdateProjectManager(value)
        },
    },

    methods: {
        ...mapMutations(['setErrorItems']),
        getRowClass(item) {
            return item.packingList.important ? 'important-row' : ''
        },
        async loadData() {
            // Load projects
            this.projects = await API.getLiteProjects({ all: true })

            // Load users
            const {
                data: { users },
            } = await API.getLiteUsers()
            this.users = users

            // Load packing lists in real-time
            await this.getPackingListsFromSDK()
        },
        async getPackingListsFromSDK() {
            try {
                this.packingLists = []

                // Set up real-time listener for active packing lists
                let query = firestore
                    .collection('companies')
                    .doc(this.companyId)
                    .collection('packingLists')

                this.packingListsListener = query.onSnapshot(
                    async snapshot => {
                        try {
                            const changes = Array.from(snapshot.docChanges())

                            changes.forEach(change => {
                                const packingList = Object.assign(
                                    change.doc.data(),
                                    {
                                        id: change.doc.id,
                                    }
                                )

                                // Filter out packing lists that have scheduledFor, shippedOn, or completedOn
                                // These are handled by the initial API load or should be excluded
                                if (
                                    packingList.scheduledFor ||
                                    packingList.shippedOn ||
                                    packingList.completedOn
                                ) {
                                    // Remove from list if it exists (it became scheduled/shipped/completed)
                                    if (
                                        change.type === 'modified' ||
                                        change.type === 'removed'
                                    ) {
                                        const index = this.packingLists.findIndex(
                                            pl => pl.id === packingList.id
                                        )
                                        if (index >= 0) {
                                            this.packingLists.splice(index, 1)
                                        }
                                    }
                                    return
                                }

                                if (
                                    change.type === 'added' ||
                                    change.type === 'modified'
                                ) {
                                    const index = this.packingLists.findIndex(
                                        pl => pl.id === packingList.id
                                    )
                                    if (index >= 0) {
                                        // Update existing packing list
                                        this.$set(
                                            this.packingLists,
                                            index,
                                            packingList
                                        )
                                    } else {
                                        this.packingLists.push(packingList)
                                    }
                                }

                                if (change.type === 'removed') {
                                    const index = this.packingLists.findIndex(
                                        pl => pl.id === packingList.id
                                    )
                                    if (index >= 0) {
                                        this.packingLists.splice(index, 1)
                                    }
                                }
                            })
                        } catch (error) {
                            console.error(
                                'Error in packing lists onSnapshot callback:',
                                error
                            )
                            this.setErrorItems({
                                source: this.$options.name,
                                message: error.message,
                            })
                        }
                    },
                    error => {
                        console.error(
                            'Firestore packing lists snapshot error:',
                            error
                        )
                        this.setErrorItems({
                            source: this.$options.name,
                            message: error.message,
                        })
                    }
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            }
        },
        formatDate(timestamp, format) {
            if (!timestamp) return ''

            // Handle both timestamp objects and string dates
            if (typeof timestamp === 'string') {
                return moment(timestamp).format(format)
            }

            let seconds
            if (timestamp.seconds) {
                seconds = timestamp.seconds
            } else if (timestamp._seconds) {
                seconds = timestamp._seconds
            } else {
                seconds = timestamp
            }

            return moment.unix(seconds).format(format)
        },
        openProcessPackingList(item) {
            this.selectedOrder = _.cloneDeep(item.packingList)
            this.selectedProject = _.cloneDeep(item.project)
            if (
                [
                    'inProgress',
                    'rejected',
                    'reserved',
                    'reservedForProject',
                    'domesticShipping',
                ].includes(item.packingList.status)
            ) {
                this.packingListForm = true
            } else {
                this.processPackingList = true
            }
        },
        closeProcessPackingList() {
            this.selectedOrder = null
            this.selectedProject = null
            this.processPackingList = false
        },
        closePackingListForm() {
            this.selectedOrder = null
            this.selectedProject = null
            this.packingListForm = false
        },
        addPackingList(newPackingList) {
            // Update the packing list in the local array
            const index = this.packingLists.findIndex(
                pl => pl.id === newPackingList.id
            )
            if (index >= 0) {
                this.packingLists.splice(index, 1, newPackingList)
            } else {
                this.packingLists.push(newPackingList)
            }
            this.packingListForm = false
        },
        replacePackingList(packingList) {
            const index = this.packingLists.findIndex(
                pl => pl.id === packingList.id
            )
            if (index >= 0) {
                this.packingLists.splice(index, 1, packingList)
            }
        },
        async refreshData() {
            await this.loadData()
        },
    },
}
</script>

<style scoped>
.no-interaction {
    pointer-events: none;
}
</style>

<style>
.important-row {
    background-color: #ffebee !important;
    border-left: 4px solid #f44336 !important;
}
.important-row:hover {
    background-color: #ffcdd2 !important;
}
.fill-height {
    height: 100%;
    min-height: 100%;
}
</style>
